import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { RotateCw, Loader2 } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Input, Select } from "../../components/common/Primitives";
import { FieldWithHelp, NumberTicker, SearchableSelect } from "../../components/common/FormPrimitives";
import api, { formatApiError } from "../../lib/api";

const types = ["Retail investor", "Angel investor", "HNI", "Family Office", "VC Fund", "PE Fund", "AIF", "NBFC", "Bank", "Strategic Investor", "Debt Fund", "Institutional Investor"];

const CITIES = [
  "Bengaluru", "Mumbai", "Delhi NCR", "Pune", "Hyderabad", "Chennai", "Kolkata",
  "Ahmedabad", "Gurgaon", "Noida", "Jaipur", "Kochi", "Lucknow", "Chandigarh",
  "Indore", "Coimbatore", "Bhubaneswar", "Vadodara", "Nagpur", "Surat", "Visakhapatnam",
  "Trivandrum", "Mangalore", "Guwahati", "Bhopal", "Ludhiana",
  "Singapore", "Dubai", "London", "New York", "Toronto", "Sydney",
];
const COUNTRIES = [
  "India", "United Arab Emirates", "Singapore", "United Kingdom", "United States",
  "Canada", "Australia", "Germany", "France", "Netherlands", "Japan", "China",
  "Hong Kong", "Israel", "Switzerland", "Sweden", "South Korea", "Malaysia",
];

function inrFmt(n) {
  const v = Number(n) || 0;
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(1)} L`;
  return `₹${v.toLocaleString("en-IN")}`;
}

export default function InvestorProfile() {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [relaunching, setRelaunching] = useState(false);

  useEffect(() => {
    api.get("/investor/me").then((r) => { setForm(r.data.investor || {}); setLoading(false); });
  }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setDirect = (k) => (v) => setForm({ ...form, [k]: v });

  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/investor/profile", form); toast.success("Profile saved."); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const relaunchWizard = async () => {
    setRelaunching(true);
    try {
      await api.post("/investor/onboarding/relaunch");
      toast.success("Wizard re-armed — heading to dashboard.");
      setTimeout(() => { window.location.href = "/investor/dashboard"; }, 500);
    } catch (err) { toast.error(formatApiError(err)); setRelaunching(false); }
  };

  if (loading) return <DashboardShell title="Investor Profile"><div className="text-ink-muted">Loading…</div></DashboardShell>;

  return (
    <DashboardShell
      title="Investor Profile"
      subtitle="INVESTOR · IDENTITY"
      actions={
        <button
          onClick={relaunchWizard}
          disabled={relaunching}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 text-xs font-heading hover:bg-emerald-50 disabled:opacity-50 transition-colors"
          data-testid="investor-relaunch-wizard"
        >
          {relaunching ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />}
          Relaunch Wizard
        </button>
      }
    >
      <Toaster richColors position="top-right" />
      <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl" data-testid="investor-profile-form">
        <Input label="Fund / Firm Name" value={form.investor_name || ""} onChange={set("investor_name")} testId="ip-name" />

        <FieldWithHelp
          label="Legal Entity"
          tooltip="The exact registered legal name of your fund / firm as it appears on incorporation and PAN docs — used for all agreements."
          testId="ip-firm"
        >
          <Input value={form.firm_name || ""} onChange={set("firm_name")} testId="ip-firm-input" />
        </FieldWithHelp>

        <Select label="Investor Type" value={form.investor_type || ""} onChange={set("investor_type")} testId="ip-type">
          <option value="">Select type</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </Select>
        <Input label="Contact Person" value={form.contact_person || ""} onChange={set("contact_person")} testId="ip-contact" />
        <Input label="Mobile" value={form.mobile || ""} onChange={set("mobile")} testId="ip-mobile" />

        <FieldWithHelp
          label="City"
          nudge="Start typing to search — pick from the list or enter a custom city."
          testId="ip-city"
        >
          <SearchableSelect
            value={form.city || ""}
            onChange={set("city")}
            options={CITIES}
            placeholder="e.g. Bengaluru"
            testId="ip-city-input"
          />
        </FieldWithHelp>

        <FieldWithHelp
          label="Country"
          nudge="Search country by name."
          testId="ip-country"
        >
          <SearchableSelect
            value={form.country || "India"}
            onChange={set("country")}
            options={COUNTRIES}
            placeholder="India"
            testId="ip-country-input"
          />
        </FieldWithHelp>

        <Input label="Website" value={form.website || ""} onChange={set("website")} testId="ip-website" />

        <FieldWithHelp
          label="AUM (₹)"
          tooltip="Assets Under Management — total capital your firm currently deploys or manages across all funds."
          nudge="Use the +/− steppers or type directly. Preview shows in Cr / L."
          testId="ip-aum"
        >
          <NumberTicker
            value={form.aum || ""}
            onChange={setDirect("aum")}
            step={10000000}
            min={0}
            placeholder="e.g. 500000000"
            testId="ip-aum-input"
            format={inrFmt}
          />
        </FieldWithHelp>

        <FieldWithHelp
          label="Portfolio Size"
          tooltip="Number of active investments currently in your portfolio (across all vintages)."
          testId="ip-portfolio"
        >
          <NumberTicker
            value={form.portfolio_size || ""}
            onChange={setDirect("portfolio_size")}
            step={1}
            min={0}
            placeholder="e.g. 25"
            testId="ip-portfolio-input"
          />
        </FieldWithHelp>

        <FieldWithHelp
          label="Years Active"
          tooltip="Number of years your fund / firm has been actively investing."
          testId="ip-years"
        >
          <NumberTicker
            value={form.years_active || ""}
            onChange={setDirect("years_active")}
            step={1}
            min={0}
            max={100}
            placeholder="e.g. 8"
            testId="ip-years-input"
          />
        </FieldWithHelp>

        <div className="md:col-span-2 flex items-center gap-3">
          <Button type="submit" data-testid="ip-save-btn">Save Profile</Button>
        </div>
      </form>
    </DashboardShell>
  );
}
